import React, { useEffect, useState } from "react";
import { Dialog, Loading } from "@actinc/dls/components";
import { Button, DialogActions, DialogContent } from "@mui/material";
import { useForm, useFormState } from "react-hook-form";
import { useClient, useMutation } from "urql";

import {
  ControlledFormInput,
  ControlledFormSelect,
  ControlledFormSwitch,
} from "../../../components";

import { lettersArray } from "../../../services/utils";

const AdminProgramDetailsDialog = ({ closeDialog, programID, programs }) => {
  const client = useClient();
  const [, mutateProgram] = useMutation(
    programID ? updateProgramMutation : createProgramMutation
  );

  const [loading, setLoading] = useState(false);
  const [program, setProgram] = useState({ ...defaultValues });

  const { control, handleSubmit, reset } = useForm({
    defaultValues: { ...defaultValues },
    mode: "all",
    reValidateMode: "onBlur",
  });
  const { isDirty, isSubmitting, isValid } = useFormState({ control });

  useEffect(() => {
    if (!programID) return;

    async function getProgram() {
      setLoading(true);
      const response = await client
        .query(getProgramQuery, { id: programID })
        .toPromise();
      const data = response.data?.programByID;

      if (data) {
        setProgram(data);

        const { configurationID, itemNamePrefix, name } = data;
        reset({
          configurationID,
          itemNamePrefix,
          name,
        });
      }

      setLoading(false);
    }

    getProgram();
  }, [programID]);

  const onSubmit = async (data) => {
    await mutateProgram(programID ? { data, id: programID } : { data }).then(
      () => closeDialog()
    );
  };

  const dialogProps = {
    draggable: false,
    fullWidth: true,
    maxWidth: "xs",
    onClose: closeDialog,
    open: true,
    scroll: "paper",
    title: programID ? `Edit Program` : `Create Program`,
  };

  return (
    <Dialog {...dialogProps}>
      <DialogContent>
        {loading && (
          <Loading
            circularProgressProps={{
              size: 24,
              thickness: 4.5,
            }}
            title="Loading..."
          />
        )}
        {!loading && (!programID || program) && (
          <form id="programAdminForm" onSubmit={handleSubmit(onSubmit)}>
            <ControlledFormInput
              control={control}
              fullWidth={true}
              id="program-name"
              inputProps={{ readOnly: !!programID }}
              label="Program Name"
              name="name"
              rules={{
                maxLength: 25,
                required: true,
                validate: (value) =>
                  validateProgramName(programs)(programID)(value) ||
                  "The program name must be unique",
              }}
            />
            <ControlledFormInput
              control={control}
              fullWidth={true}
              id="configuration-id"
              label="Configuration Id"
              name="configurationID"
              rules={{
                maxLength: 50,
                required: false,
              }}
            />
            <ControlledFormSelect
              control={control}
              fullWidth={true}
              id="program-itemNamePrefix"
              label="Item Name Identifier"
              name="itemNamePrefix"
              options={itemNamePrefixes}
              rules={{
                required: true,
              }}
            />
          </form>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={closeDialog} variant="text">
          Cancel
        </Button>
        <Button
          disabled={(!programID && !isDirty) || !isValid || isSubmitting}
          form="programAdminForm"
          type="submit"
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const programFields = `
  id
  cdeID
  configurationID
  itemNamePrefix
  name
`;

const createProgramMutation = `
  mutation CreateProgram($data: ProgramInput!) {
    createProgram(data: $data) { ${programFields} }
  }`;
const getProgramQuery = `
  query GetProgram($id: ID!) {
    programByID(id: $id) { ${programFields} }
  }`;
const updateProgramMutation = `
  mutation UpdateProgram($id: ID!, $data: ProgramInput!) {
    updateProgram(id: $id, data: $data) { ${programFields} }
  }`;

const defaultValues = {
  configurationID: "",
  itemNamePrefix: "",
  keywords: [],
  name: "",
};
const itemNamePrefixes = lettersArray.map((value) => ({
  label: value,
  value,
}));

const validateProgramName = (programs) => (programID) => (value) => {
  return !programs.find((p) => p.id !== programID && p.name === value);
};

export {
  AdminProgramDetailsDialog,
  createProgramMutation,
  getProgramQuery,
  updateProgramMutation,
};
